import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ArrowRight, Mail, User, FileText, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import api from '@/utils/api';

const STEP_MAP = { 'email_verification': 1, 'profile': 2, 'nda': 3, 'complete': 4 };

const OnboardPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = searchParams.get('code') || '';
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Registration
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Verification
  const [verifyCode, setVerifyCode] = useState('');

  // Profile
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [aadhar, setAadhar] = useState('');
  const [pan, setPan] = useState('');

  // NDA
  const [ndaContent, setNdaContent] = useState('');
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);

  // Completed user data
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (inviteCode) validateInvite();
    // Check if user has session and resume
    const token = localStorage.getItem('session_token');
    if (token) resumeOnboarding();
  }, []);

  const validateInvite = async () => {
    try {
      const res = await api.get(`/invite/validate/${inviteCode}`);
      if (res.data.email) {
        setInviteEmail(res.data.email);
        setEmail(res.data.email);
      }
    } catch (e) {
      // Invalid invite - still allow self-onboarding
    }
  };

  const resumeOnboarding = async () => {
    try {
      const res = await api.get('/onboard/status');
      const status = res.data.onboarding_status;
      if (status && status !== 'complete' && STEP_MAP[status] !== undefined) {
        setStep(STEP_MAP[status]);
        setUserData(res.data);
        if (status === 'nda') loadNda();
      } else if (status === 'complete') {
        setStep(4);
        setUserData(res.data);
      }
    } catch (e) {
      // No valid session - stay at step 0
    }
  };

  const loadNda = async () => {
    try {
      const res = await api.get('/nda/template');
      setNdaContent(res.data.content || '');
    } catch (e) { /* optional */ }
  };

  const handleRegister = async () => {
    if (!email || !password || !firstName || !lastName) {
      toast.error('All fields are required');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/onboard/register', {
        email, password, first_name: firstName, last_name: lastName, invite_code: inviteCode
      });
      localStorage.setItem('session_token', res.data.session_token);
      
      // If resuming, jump to the right step
      const status = res.data.onboarding_status;
      if (status && STEP_MAP[status] !== undefined) {
        setStep(STEP_MAP[status]);
        if (status === 'nda') loadNda();
      } else {
        setStep(1);
      }
      
      toast.success(res.data.message || 'Account created!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyCode) { toast.error('Enter the verification code'); return; }
    setLoading(true);
    try {
      await api.post('/onboard/verify-email', { code: verifyCode });
      toast.success('Email verified!');
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleProfile = async () => {
    if (!phone || !address || !pan) { toast.error('Phone, address, and PAN are required'); return; }
    setLoading(true);
    try {
      await api.post('/onboard/profile', { phone, address, aadhar, pan });
      toast.success('Profile saved!');
      loadNda();
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleSignNda = async () => {
    if (!typedName || !agreed) { toast.error('Type your name and agree'); return; }
    setLoading(true);
    try {
      await api.post('/nda/sign', { typed_name: typedName, agreed: true });
      await api.post('/onboard/complete');
      toast.success('Onboarding complete!');
      // Load user data for completion screen
      try {
        const res = await api.get('/onboard/status');
        setUserData(res.data);
      } catch (e) {}
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ['Register', 'Verify', 'Profile', 'NDA', 'Done'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-violet-700">Graphikpro</h1>
          <p className="text-sm text-muted-foreground">Translator Onboarding</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1 mb-6">
          {stepLabels.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? 'bg-green-500 text-white' : i === step ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              {i < stepLabels.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {step === 0 && <><User className="w-5 h-5" /> Create Account</>}
              {step === 1 && <><Mail className="w-5 h-5" /> Verify Email</>}
              {step === 2 && <><User className="w-5 h-5" /> Your Details</>}
              {step === 3 && <><FileText className="w-5 h-5" /> Sign NDA</>}
              {step === 4 && <><Shield className="w-5 h-5" /> You're Onboarded!</>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Step 0: Register */}
            {step === 0 && (
              <>
                {inviteEmail && <Badge variant="outline" className="mb-2">Invited: {inviteEmail}</Badge>}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>First Name</Label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" data-testid="onboard-first-name" />
                  </div>
                  <div className="space-y-1">
                    <Label>Last Name</Label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" data-testid="onboard-last-name" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" disabled={!!inviteEmail} data-testid="onboard-email" />
                </div>
                <div className="space-y-1">
                  <Label>Password</Label>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password" data-testid="onboard-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button onClick={handleRegister} disabled={loading} className="w-full" data-testid="onboard-register-btn">
                  {loading ? 'Creating...' : 'Create Account'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Already have an account? <a href="/login" className="text-violet-600 underline">Login</a>
                </p>
              </>
            )}

            {/* Step 1: Verify Email */}
            {step === 1 && (
              <>
                <p className="text-sm text-muted-foreground">We sent a 6-digit verification code to your email. Please check your inbox.</p>
                <div className="space-y-1">
                  <Label>Verification Code</Label>
                  <Input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} placeholder="123456" maxLength={6} className="text-center text-2xl tracking-widest" data-testid="onboard-verify-code" />
                </div>
                <Button onClick={handleVerify} disabled={loading} className="w-full" data-testid="onboard-verify-btn">
                  {loading ? 'Verifying...' : 'Verify & Continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {/* Step 2: Profile */}
            {step === 2 && (
              <>
                <div className="space-y-1">
                  <Label>Phone Number</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="onboard-phone" />
                </div>
                <div className="space-y-1">
                  <Label>Address</Label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full address" data-testid="onboard-address" />
                </div>
                <div className="space-y-1">
                  <Label>Aadhar Number</Label>
                  <Input value={aadhar} onChange={(e) => setAadhar(e.target.value)} placeholder="1234 5678 9012" data-testid="onboard-aadhar" />
                </div>
                <div className="space-y-1">
                  <Label>PAN Number</Label>
                  <Input value={pan} onChange={(e) => setPan(e.target.value)} placeholder="ABCDE1234F" data-testid="onboard-pan" />
                </div>
                <Button onClick={handleProfile} disabled={loading} className="w-full" data-testid="onboard-profile-btn">
                  {loading ? 'Saving...' : 'Continue to NDA'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {/* Step 3: NDA */}
            {step === 3 && (
              <>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-3 text-sm bg-muted/30" dangerouslySetInnerHTML={{ __html: ndaContent || '<p>Loading NDA...</p>' }} />
                <div className="space-y-1">
                  <Label>Type Your Full Name to Sign</Label>
                  <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="John Doe" data-testid="onboard-nda-name" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                  I have read and agree to the Non-Disclosure Agreement
                </label>
                <Button onClick={handleSignNda} disabled={loading || !agreed || !typedName} className="w-full" data-testid="onboard-sign-btn">
                  {loading ? 'Signing...' : 'Sign NDA & Complete'} <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {/* Step 4: Complete */}
            {step === 4 && (
              <div className="text-center py-4">
                <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-bold mb-2">You're Officially Onboarded!</h3>
                <p className="text-muted-foreground mb-4">Welcome to Team Graphikpro. Our team will connect with you shortly.</p>
                {userData && (
                  <div className="text-left bg-muted/50 rounded-lg p-4 mb-4 space-y-1 text-sm">
                    <p><strong>Name:</strong> {userData.name}</p>
                    <p><strong>Email:</strong> {userData.email}</p>
                  </div>
                )}
                <Button onClick={() => { localStorage.removeItem('session_token'); navigate('/login'); }} className="w-full" data-testid="onboard-go-login">
                  Go to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardPage;
